import * as THREE from 'three';
import { CAMERA } from '../core/SimConfig.js';

const _offsetWorld = new THREE.Vector3();
const _lookQuat = new THREE.Quaternion();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _desiredPos = new THREE.Vector3();
const _lookahead = new THREE.Vector3();
const _bodyEuler = new THREE.Euler(0, 0, 0, 'YXZ');
const _bodyTarget = new THREE.Quaternion();
const _lift = new THREE.Vector3();
const _headQuat = new THREE.Quaternion();

export default class ChaseCamera {
  constructor({ aircraft, controller, terrainCollider = null }) {
    this.aircraft = aircraft;
    this.controller = controller;
    this.terrainCollider = terrainCollider;

    // Output — CameraManager reads these
    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    this.offset = new THREE.Vector3(...CAMERA.chase.offset);

    // Set by CameraManager when the A-10 is destroyed: pulls the follow distance
    // out to CAMERA.chase.destroyedDistance for a wide wreck-watching shot.
    this.destroyed = false;

    // Smoothing rates (higher = snappier, lower = lazier)
    this.lookSmoothing = CAMERA.chase.lookSmoothing;
    this.positionSmoothing = CAMERA.chase.positionSmoothing;
    this.positionSmoothingY = CAMERA.chase.positionSmoothingY;

    // Speed-based lookahead: anchor shifts forward along velocity
    this.lookaheadScale = CAMERA.chase.lookaheadScale;
    this.lookaheadMax = CAMERA.chase.lookaheadMax;

    // Right-click zoom blend (0 = normal, 1 = fully zoomed). Eased at the shared
    // FOV rate so the pull-back tracks CameraManager's FOV lerp instead of the
    // airframe swelling and then settling back as the two race each other.
    this._zoomBlend = 0;

    // Smoothed look state
    this._smoothYaw = controller.lookYaw;
    this._smoothPitch = controller.lookPitch;

    // Manual schemes: a lagged copy of the airframe's attitude (with the head
    // look on top) that the camera hangs behind, and the 0..1 blend toward it
    // from the level look-to-steer framing. Quaternion throughout, so going
    // vertical in a loop has no gimbal flip to hit.
    this._bodyQuat = new THREE.Quaternion();
    this._frameBlend = controller.bodyLook ? 1 : 0;

    // Smoothed offset from aircraft (eliminates velocity-dependent lag)
    this._smoothOffset = new THREE.Vector3();
    this._initialized = false;
  }

  /** Jump straight to the controller's look frame (no glide) — at launch. */
  snapFrame() {
    this._frameBlend = this.controller.bodyLook ? 1 : 0;
  }

  update(dt) {
    const ctrl = this.controller;
    const aircraft = this.aircraft;

    // ── Zoom blend ───────────────────────────────────────────────────────
    const zoomAlpha = 1 - Math.exp(-CAMERA.fovSmoothing * dt);
    this._zoomBlend += ((ctrl.zooming ? 1 : 0) - this._zoomBlend) * zoomAlpha;

    // ── Smooth look ──────────────────────────────────────────────────────
    const lookAlpha = 1 - Math.exp(-this.lookSmoothing * dt);
    this._smoothYaw += (ctrl.lookYaw - this._smoothYaw) * lookAlpha;
    this._smoothPitch += (ctrl.lookPitch - this._smoothPitch) * lookAlpha;

    // Orient camera from smoothed look direction
    _euler.set(this._smoothPitch, this._smoothYaw, 0);
    _lookQuat.setFromEuler(_euler);

    // Airframe-following orientation (manual schemes)
    const cfg = CAMERA.chase;
    _bodyEuler.set(ctrl.headPitch + cfg.bodyPitch, ctrl.headYaw, 0);
    _bodyTarget.copy(aircraft.quaternion).multiply(_headQuat.setFromEuler(_bodyEuler));
    if (!this._initialized) this._bodyQuat.copy(_bodyTarget);
    else this._bodyQuat.slerp(_bodyTarget, 1 - Math.exp(-cfg.bodyFollowSmoothing * dt));
    const frameAlpha = 1 - Math.exp(-CAMERA.cockpit.frameBlendRate * dt);
    // A tumbling wreck is watched from the level framing whatever the scheme.
    const bodyWanted = ctrl.bodyLook && !this.destroyed;
    this._frameBlend += ((bodyWanted ? 1 : 0) - this._frameBlend) * frameAlpha;
    const fb = this._frameBlend;
    if (fb > 1e-3) _lookQuat.slerp(this._bodyQuat, fb > 0.999 ? 1 : fb);
    this.quaternion.copy(_lookQuat);

    // ── Smooth position ──────────────────────────────────────────────────
    // Desired offset from aircraft: behind the smoothed look direction. A
    // destroyed A-10 pulls back to the wider wreck-watching distance; the
    // position smoothing eases the camera out rather than snapping.
    // Zoom scales whichever base distance applies, so the wreck-watching shot
    // telephotos the same way a live chase does.
    const baseDist = this.destroyed ? CAMERA.chase.destroyedDistance : this.offset.length();
    const dist = baseDist * (1 + (CAMERA.chase.zoomDistanceScale - 1) * this._zoomBlend);
    _offsetWorld.set(0, 0, 1).applyQuaternion(_lookQuat).multiplyScalar(dist);
    // Shift anchor forward along aircraft velocity so camera looks ahead
    const lookaheadDist = Math.min(aircraft.airspeed * this.lookaheadScale, this.lookaheadMax);
    _lookahead.copy(aircraft.forwardVector).multiplyScalar(lookaheadDist);

    // Desired offset = lookahead + camera-behind offset + vertical boost
    _desiredPos.copy(_lookahead).add(_offsetWorld);
    // Lift above the airframe: straight up for the level framing, along the
    // camera's own up when it's riding the attitude (so it stays 'above' the
    // jet inverted or vertical).
    _lift.set(0, 1, 0).applyQuaternion(_lookQuat).multiplyScalar(cfg.bodyLift * fb);
    _lift.y += 3 * (1 - fb);
    _desiredPos.add(_lift);

    if (!this._initialized) {
      this._smoothOffset.copy(_desiredPos);
      this._initialized = true;
    } else {
      const posAlpha = 1 - Math.exp(-this.positionSmoothing * dt);
      // The slower vertical smoothing suits a level chase; following the
      // attitude through a loop it would just drag the camera off the axis.
      const posAlphaY = 1 - Math.exp(-(this.positionSmoothingY + (this.positionSmoothing - this.positionSmoothingY) * fb) * dt);
      this._smoothOffset.x += (_desiredPos.x - this._smoothOffset.x) * posAlpha;
      this._smoothOffset.z += (_desiredPos.z - this._smoothOffset.z) * posAlpha;
      this._smoothOffset.y += (_desiredPos.y - this._smoothOffset.y) * posAlphaY;
    }

    // Apply smoothed offset to current aircraft position (no velocity lag)
    this.position.copy(aircraft.position).add(this._smoothOffset);
    const groundY = this.terrainCollider
      ? (this.terrainCollider.getHeightAt(this.position.x, this.position.z) ?? 0)
      : 0;
    this.position.y = Math.max(this.position.y, groundY + 1.5);
  }
}
